import React, { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { buildWireDrawdownEvents } from '~/app/pages/Transfers/_utils/wireDrawdown';
import { ROUTE } from '~/app/routes';
import { CopyInput, Details, Loading, PageHeader, RenderFields } from '~/components';
import { useAccountNumber, useBankAccount, useCounterparty, useEntity } from '~/hooks';
import { useTransfer } from '~/hooks/useTransfers';
import { useNavigate } from '~/lib/navigation';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { WireDrawdownRequest } from '~/typings/API';

import { InfoForm, TransferPageHeader } from './_components/Layout';
import { Timeline } from './_components/Timeline';

interface WireDrawdownPageProps {
  id: string;
}

const WireDrawdownPage: FC<WireDrawdownPageProps> = ({ id }) => {
  const { isAuthorized } = useSessionStore();
  const navigate = useNavigate();
  const { addDangerNotification } = useNotificationStore();

  const {
    response: bankAccount,
    isLoading: isBankAccountLoading,
    createRequest: fetchBankAccount,
  } = useBankAccount({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: accountNumber,
    isLoading: isAccountNumberLoading,
    createRequest: fetchAccountNumber,
  } = useAccountNumber({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: counterparty,
    isLoading: isCounterpartyLoading,
    createRequest: fetchCounterparty,
  } = useCounterparty({
    onError: (error) => addDangerNotification({ content: error.message }),
  });

  const [isLoadingEntity, setIsLoadingEntity] = useState(false);
  const { response: entity, createRequest: fetchEntity } = useEntity({
    onSuccess: () => setIsLoadingEntity(false),
    onError: (error) => {
      setIsLoadingEntity(false);
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: transfer,
    isLoading: isTransferLoading,
    createRequest: fetchTransfer,
  } = useTransfer<WireDrawdownRequest>('wire-drawdown')({
    initialParams: { id },
    onSuccess: async (response) => {
      fetchAccountNumber({ id: response.accountNumberId });
      try {
        if (response.bankAccountId) {
          const currentBankAccount = await fetchBankAccount({ id: response.bankAccountId });
          if (!currentBankAccount) return;
          if (isAuthorized({ permission: 'entities', permissionLevel: 'read' })) {
            fetchEntity({ id: currentBankAccount.owners?.[0] });
            setIsLoadingEntity(true);
          }
        }
      } catch (error) {
        addDangerNotification({ content: (error as Error).message });
      }
      if (response.beneficiaryCounterpartyId) {
        fetchCounterparty({ id: response.beneficiaryCounterpartyId });
      }
    },
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  useEffect(() => {
    if (id) {
      fetchTransfer({ id });
    }
  }, [id]);

  const formattedTransfer = useMemo(() => {
    if (!transfer) return undefined;
    const formatted: Record<string, unknown> = {};
    Object.entries(transfer).forEach(([key, value]) => {
      formatted[key] = value;
    });
    return formatted;
  }, [transfer]);
  const formMethods = useForm<WireDrawdownRequest>();

  const isDataLoading = useMemo(() => {
    return isAccountNumberLoading || isBankAccountLoading || isCounterpartyLoading || isTransferLoading;
  }, [isAccountNumberLoading, isBankAccountLoading, isCounterpartyLoading, isTransferLoading]);

  const timelineEntries = useMemo(() => {
    if (!bankAccount || !accountNumber || !counterparty || !transfer || isLoadingEntity) {
      return [];
    }
    return buildWireDrawdownEvents({
      transfer,
      account: {
        accountNumber,
        bankAccount,
        entityType: entity ? (entity.type.toLowerCase() as 'person' | 'business') : undefined,
      },
      counterparty,
    });
  }, [bankAccount, accountNumber, counterparty, transfer, isLoadingEntity, entity]);

  return (
    <>
      <PageHeader text="Transfers" backButton />
      <Loading isLoading={isDataLoading} fullHeight>
        {transfer && (
          <TransferPageHeader
            amount={transfer.amount!}
            isAdding={transfer ? transfer.isIncoming === false : undefined}
            direction={{ type: transfer.isIncoming ? 'incoming' : 'outgoing' }}
            status={transfer.status!}
            transferId={transfer.id!}
            transferType="wire-drawdown"
          />
        )}
        {timelineEntries.length > 0 && <Timeline entries={timelineEntries} />}
        <FormProvider {...formMethods}>
          <InfoForm autoComplete="off">
            {RenderFields<WireDrawdownRequest>({
              sections: [
                {
                  headline: 'Information',
                  fields: [
                    {
                      id: 'imad',
                      label: 'IMAD',
                      tooltip:
                        'IMAD (Input/Output Message Accountability Data) is a unique number set by the originating bank given to each FedWire payment when using the Federal Reserve Bank Service and can be used to investigate and track wire transfers. IMAD will not be populated for on-us wire transfers.',
                      children: ({ value }) => {
                        return <CopyInput value={value ? String(value) : '-'} />;
                      },
                    },
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value }) => {
                        return <CopyInput value={value ? String(value) : '-'} />;
                      },
                    },
                  ],
                },
              ],
            })}
            <Details
              label="Transfer Details"
              formattedData={formattedTransfer}
              rawData={transfer as Record<string, unknown> | undefined}
            />
          </InfoForm>
        </FormProvider>
      </Loading>
    </>
  );
};

interface Params {
  id: string;
}

const PageTransfersWireDrawdown: FC = () => {
  const { id } = useParams<keyof Params>() as Params;
  return <WireDrawdownPage id={id} />;
};

export default PageTransfersWireDrawdown;
